import Vue from "vue";
import Vuex, { Store } from "vuex";
import axios, { AxiosError, AxiosResponse } from "axios";
import VuexPersistence from "vuex-persist";
import {
  SnackbarBody,
  LookupTable,
  LookupTableRequestState,
  UpdateInputRequest,
  FileToUpload,
  LookupTableDataResponse,
  GetLookupTableInput,
  SearchDependenciesArgs,
  RequestBodyErrorPayload,
  PageTypes,
  WorklogsConfig,
  logTypes
} from "./../../models/MiscTypes";
import { 
  Translations,
  LangCodes,
  SetTranslationsPayload
} from "./../../translations/Translations";
import { populateTranslations } from "./../../translations/Translations";
import Wraxios from "./../../services/Wraxios";
import omit from "lodash/omit";
import { addGlow } from "./../../services/SharedFunctions";
import { cloneDeep, isEmpty } from "lodash";
import { MaintReqState } from "./MaintReqState";

const vuexLocal = new VuexPersistence({
  key: "maintreq",
  storage: window.localStorage
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    userIsAuthenticated: false as boolean,
    userSessionHash: "" as string,
    userLoginTime: "" as string,
    userData: {} as { [key: string]: string },
    translations: {} as { [key: string]: Translations },
    defaultText: {} as { [key: string]: string | number },
    language: "" as LangCodes,
    snackbars: [] as SnackbarBody[],
    lookupTableData: {
      isLoading: false,
      headers: [] as Array<object>,
      tableData: [],
      show: false,
      error: false,
      inputId: "",
      lookupTableRequestState: {} as LookupTableRequestState,
      searchFields: {},
      valueToSubmit: ""
    } as LookupTable,
    // lookupTableSearchDependences object -> formId: { maxAttribute: {search Deps}}
    lookupTableSearchDependencies: {} as {[key: string]: {[key: string]: Record<string, unknown>}},
    missingRequiredAttributes: [] as string[],
    // newRequestBody -> { formId: { maxAttribute 1: value, maxAttribute2: value } }
    newRequestBody: {} as { [key: string]: { [key: string]: string | FileToUpload[] | boolean } },
    // newRequestBodyErrors -> { formId: { primaryemail: true, firstname: false } }
    newRequestBodyErrors: {} as { [key: string]: { [key: string]: boolean } },
    showOverlay: false as boolean,
    userSessionInvalidated: false as boolean,
    // maximoConnectionIssues is set to true when we receive 503 responses from the server
    maximoConnectionIssues: false as boolean,
    dependenciesObject: {} as {[key: string]: {[key: string]: string[]} },
    // discrepancies are values submitted by users, but were automactially modified by maximo
    discrepancies: {} as {[key: string]: string},
    lookupTableDefaultFilters: {} as {[key: string]: string},
    isRegistrationDisabled: false as boolean,
    isNewWorklogClicked: false,
    worklogs: {} as WorklogsConfig,
    logTypes: [] as logTypes[],
    isInquiryTableRowExpanded: false
  } as MaintReqState,

  getters: {
    get_userIsAuthenticated: (state: MaintReqState) => {
      return state.userIsAuthenticated;
    },

    get_userSessionHash: (state: MaintReqState) => {
      return state.userSessionHash;
    },

    get_userLoginTime: (state: MaintReqState) => {
      return state.userLoginTime;
    },

    get_userData: (state: MaintReqState) => {
      return state.userData;
    },

    get_snackbars: (state: MaintReqState) => {
      return state.snackbars;
    },

    get_language: (state: MaintReqState) => {
      return state.language;
    },

    get_translations: (state: MaintReqState) => {
      if (state.translations && state.translations[state.language]) {
        return state.translations[state.language];
      } else {
        return state.defaultText;
      }
    },

    get_lookupTableData: (state: MaintReqState) => state.lookupTableData,

    get_missingRequiredAttributes: (state: MaintReqState) => state.missingRequiredAttributes,

    get_newRequestBody: (state: MaintReqState) => state.newRequestBody,

    get_showOverlay: (state: MaintReqState) => state.showOverlay,
    
    get_userSessionInvalidated: (state: MaintReqState) => state.userSessionInvalidated,

    get_maximoConnectionIssues: (state: MaintReqState) => state.maximoConnectionIssues,

    get_newRequestBodyErrors: (state: MaintReqState) => state.newRequestBodyErrors,

    get_dependenciesObject: (state: MaintReqState) => state.dependenciesObject,
    
    get_discrepancies: (state: MaintReqState) => state.discrepancies,

    get_lookupTableDefaultFilters: (state: MaintReqState) => state.lookupTableDefaultFilters,

    get_lookupTableSearchDependencies: (state: MaintReqState) => state.lookupTableSearchDependencies,

    get_isRegistrationDisabled: (state: MaintReqState) => state.isRegistrationDisabled,

    get_isNewWorklogClicked: (state: MaintReqState) => state.isNewWorklogClicked,

    get_worklogs: (state: MaintReqState) => state.worklogs,

    get_logTypes: (state: MaintReqState) => state.logTypes,

    get_isInquiryRowExpanded: (state: MaintReqState) => state.isInquiryTableRowExpanded
  },

  mutations: {
    set_userIsAuthenticated: (state: MaintReqState, payload: boolean) => {
      state.userIsAuthenticated = payload;
    },

    set_userSessionHash: (state: MaintReqState, payload: string) => {
      state.userSessionHash = payload;
    },

    set_userLoginTime: (state: MaintReqState, payload: string) => {
      state.userLoginTime = payload;
    },

    set_userData: (state: MaintReqState, payload: { [key: string]: string }) => {
      state.userData = payload;
    },

    set_snackbars: (state: MaintReqState, payload: SnackbarBody[]) => {
      state.snackbars = payload;
    },

    push_snackbar: (state: MaintReqState, payload: SnackbarBody) => {
      state.snackbars.push(payload);
    },

    set_language: (state: MaintReqState, payload: LangCodes) => {
      state.language = payload;
    },

    set_translations: (state: MaintReqState, payload: SetTranslationsPayload) => {
      state.translations[payload.langCode] = populateTranslations(payload.translations);
    },

    set_defaultText: (state: MaintReqState, payload: { [key: string]: string | number }) => {
      state.defaultText = payload;
    },

    set_lookupTableData: (state: MaintReqState, payload: LookupTable) => {
      state.lookupTableData = payload;
    },   

    set_missingRequiredAttributes: (state: MaintReqState, payload: string[]) => {
      state.missingRequiredAttributes = payload;
    },
    set_defaultRequestBody: (state: MaintReqState) => {
      state.newRequestBody = {};
      state.newRequestBodyErrors = {};
    },
    update_newRequestBody: (state: MaintReqState, payload: [string, { [key: string]: string | FileToUpload[] }]) => {
      const formId = payload[0] as string;
      Vue.set(state.newRequestBody, formId, {
        ...state.newRequestBody[formId],
        ...payload[1]
      });
    },

    // Removes a file from the payload
    // Payload: tuple<string,string, string>
    // Payload[0] = formId
    // Payload[1] = fileName
    // Payload[2] = maxAttribute (almost definitely always going to be doclinks)
    remove_file_from_requestBody: (state: MaintReqState, payload: [string, string, string]) => {
      const formId = payload[0] as string;
      const fileName = payload[1] as string;
      const maxAttribute = payload[2] as string;
      const doclinksArr = state.newRequestBody[formId][maxAttribute] as FileToUpload[];

      for (let i = 0; i < doclinksArr.length; i++) {
        if ((doclinksArr[i] as FileToUpload).name.toLocaleLowerCase() === fileName.toLocaleLowerCase()) {
          doclinksArr.splice(i, 1);

          if (isEmpty(doclinksArr)) {
            delete state.newRequestBody[formId][maxAttribute];
          }

          return;
        }
      }

      // Maybe route back to home page because this is an issue.
      throw "Could not remove file from upload list.";
    },

    clear_newRequestBody_formId(state: MaintReqState, formId: string) {
      delete state.newRequestBody[formId];
    },

    set_showOverlay: (state: MaintReqState, payload: boolean) => state.showOverlay = payload,

    set_userSessionInvalidated: (state: MaintReqState, payload: boolean) => state.userSessionInvalidated = payload,

    set_maximoConnectionIssues: (state: MaintReqState, payload: boolean) => state.maximoConnectionIssues = payload,

    set_newRequestBodyErrors: (state: MaintReqState, payload: RequestBodyErrorPayload) => {
      const formId = payload.formId;
      Vue.set(state.newRequestBodyErrors, formId, {
        ...state.newRequestBodyErrors[formId],
        ...payload.payload
      });
    },

    set_dependenciesObject: (state: MaintReqState, payload: [string, { [key: string]: string[] }]) => {
      const formId = payload[0] as string;
      state.dependenciesObject[formId] = payload[1];
    },      

    set_discrepancies: (state: MaintReqState, payload: {[key: string]: string}) => {
      state.discrepancies = payload;
    },

    set_lookupTableDefaultFilters: (state: MaintReqState, payload: {[key: string]: string}) =>
      state.lookupTableDefaultFilters = payload,

    set_lookupTableSearchDependencies: (state: MaintReqState, payload: SearchDependenciesArgs) => {
      const formId = payload.formId;
      const maxAttribute = payload.maxAttribute;
      const searchDependencies = payload.searchDependencies;

      const updateObj = {
        ...state.lookupTableSearchDependencies[formId],
        [maxAttribute]: searchDependencies
      };
      
      state.lookupTableSearchDependencies[formId] = updateObj;
    },
    
    resetLookupTableSearchDependencies: (state: MaintReqState) => state.lookupTableSearchDependencies = {},

    set_isRegistrationDisabled: (state: MaintReqState, payload: boolean) => state.isRegistrationDisabled = payload,

    set_isNewWorklogClicked: (state: MaintReqState, payload: boolean) => state.isNewWorklogClicked = payload,

    set_worklogs: (state: MaintReqState, payload: WorklogsConfig) => state.worklogs = payload,

    set_logTypes: (state: MaintReqState, payload: logTypes[]) => state.logTypes = payload,

    set_isInquiryTableRowExpanded: (state: MaintReqState, payload: boolean) => state.isInquiryTableRowExpanded = payload
  },

  actions: {
    do_setUserIsAuthenticated(context, payload: boolean) {
      context.commit("set_userIsAuthenticated", payload);
    },

    do_setUserSessionHash(context, payload: string) {
      context.commit("set_userSessionHash", payload);
    },

    do_setUserLoginTime(context, payload: string) {
      context.commit("set_userLoginTime", payload);
    },

    do_setUserData(context, payload: { [key: string]: string }) {
      context.commit("set_userData", payload);
    },

    do_setDefaultText(context, payload: { [key: string]: string }) {
      context.commit("set_defaultText", payload);
    },

    do_showSnackbar(context, payload: SnackbarBody) {
      try {
        if (payload.text.startsWith("<!DOCTYPE html>")) {
          return;
        }

        if (payload.text.startsWith("System.")) {
          context.commit("push_snackbar", {
            text: "Something went wrong. Please try again or contact support.",
            color: "error"
          });
          
          return;
        }

        context.commit("push_snackbar", {
          text: payload.text,
          color: payload.color,
        });
      } catch (error) {
        return;
      }      
    },

    async textInputLookup(context, input: GetLookupTableInput) {
      // Because of the nature of this application, there's a very
      // high probability of having duplicate element IDs on an input
      // form. To help differentiate an input for submitting a new
      // request vs any other input, such as a lookup field in a data 
      // table, `-input` is appended to the ID of an input element on 
      // any new request form. We have to strip out the `-input` to 
      // isolate the value that actually represents the maxAttribute
      // that we're trying to work with.
      const maxAttribute = input.maxAttribute?.substring(0, input.maxAttribute.indexOf("-input")) as string;
      const requestBody = cloneDeep(input);

      requestBody.maxAttribute = maxAttribute;

      context.commit("set_lookupTableData", {
        ...this.getters.get_lookupTableData,
        show: true,
        isLoading: true,
        headers: [],
        tableData: [],
        lookupTableRequestState: {} as LookupTableRequestState
      });

      // eslint-disable-next-line
      function handle200(context: Store<any>, response: AxiosResponse, maxAttrId?: string | undefined) {
        const result = response.data as LookupTableDataResponse;

        const tableHeaders = [] as Array<object>;
        result.headers.forEach((item: object, index: number) => {
          tableHeaders.push({ text: item, value: result.fields[index] });
        });

        const searchFields = result.lookupTableSearchDependencies?.customFilters;

        context.commit("set_lookupTableData", {
          ...context.getters["get_lookupTableData"],
          isLoading: false as boolean,
          error: false as boolean,
          headers: tableHeaders as Array<object>,
          tableData: result.tableData,
          inputId: maxAttrId as string,
          lookupTableRequestState: result.lookupTableRequestState as LookupTableRequestState,
          searchFields: searchFields,
          valueToSubmit: result.valueToSubmit as string
        } as LookupTable);

        const searchDepsPayload: SearchDependenciesArgs = {
          formId: input.formId as string,
          maxAttribute: maxAttribute as string,
          searchDependencies: result.lookupTableSearchDependencies
        };

        context.commit("set_lookupTableSearchDependencies", searchDepsPayload);
        
        if (result.lookupTableSearchDependencies !== null) {
          if (result.lookupTableSearchDependencies.defaultFilters !== null) {
            context.commit("set_lookupTableDefaultFilters", 
            result.lookupTableSearchDependencies.defaultFilters);
          }
        }
      }

      // eslint-disable-next-line
      function handleError(context: Store<any>, response: AxiosError) {
        context.commit("set_lookupTableData", {
          ...context.getters["get_lookupTableData"],
          show: false,
          isLoading: false,
          headers: [],
          tableData: [],
          error: true,
          lookupTableRequestState: {},
          searchFields: {}
        } as LookupTable);

        context.commit("push_snackbar", {
          text: "There was a problem getting data from the server. Please try again or contact support.",
          color: "error"
        } as SnackbarBody);
      }

        const wraxios = new Wraxios();

        await wraxios.post(
          axios,
          input.lookupTableUrl as string,
          {
            maxAttribute: maxAttribute as string,
            pageName: input.pageName as string,
            pageType: input.pageType as PageTypes,
            searchParams: input.searchParams
          },
          {
            200: (response: AxiosResponse) => handle200(this, response, input.maxAttribute),
            400: (error: AxiosError) => handleError(this, error),
            500: (error: AxiosError) => handleError(this, error)
          }
        );
    },

    // This function is called by the inputs whenever their value is updated
    // It reaches out to the back end which will then reach out to Maixmo to validate
    // that data is good and will return all currently valid inputs and any related data
    // for multi-part inputs
    async updateInputData(context, payload: [UpdateInputRequest, string]) {
      const input = payload[0];
      const formId = payload[1] as string;
      
      if (input.pageType === PageTypes.EditPage) {
        // Edit pages are either readonly or indicate a worklog,
        // neither of which get validated.
        return;
      }
      
      const wraxios = new Wraxios();      
      const updateBody = omit(input, "callbacks");
      const requestBodyBeforeUpdate = (this.getters.get_newRequestBody)[formId];

      // We create a function that will update our request body with the data returned from
      // the validation process. This can include related data that is not yet displayed on the page.
      // Inputs have watchers that check for updates to the new request body and will update their value
      // with the value in the new request body if it changes (this happens with related data i.e. 
      // asset.description might update after a new asset has been selected)
      const updateRequestBodyWithResponse = (response: AxiosResponse) => {
        const newVals = response.data as Record<string, string>;

        Object.keys(newVals).forEach(maxAttribute => {
          const newKVP = {} as Record<string, string>;
          newKVP[maxAttribute] = response.data[maxAttribute];
          context.commit("update_newRequestBody", [formId, newKVP]);
        });

        // Grab all values that are present in the current request body, but are not present in the new request body
        // modified by Maximo.
        // Maximo can remove maxattribute:value pairs from the request body for instance:
        //  Having a dropdown for SR.commoditygroup and SR.commmodity, changing commoditygroup will usually result in Maximo
        //  resetting the value of commodity to empty (in our case, it completely removes it from the API response)
        const removedValues = Object.keys(context.state.newRequestBody[formId])
          .filter((maxAttr) => (Object.keys(newVals).indexOf(maxAttr) == -1 && maxAttr !== "doclinks" && maxAttr !== "description_longdescription"));
        
        removedValues.forEach(maxAttr => {
          const oldValue = context.state.newRequestBody[formId][maxAttr];

          Vue.delete(context.state.newRequestBody[formId], maxAttr);

          // In the case that the old value was not falsy, we add the glow so the user knows it was removed.
          if (!!oldValue) {
            addGlow(maxAttr, formId);
          }

        });

        const relatedData = omit(newVals, input.inputId);
        Object.keys(relatedData).forEach(maxAttr => {
          if (requestBodyBeforeUpdate[maxAttr] !== newVals[maxAttr]) {
            addGlow(maxAttr, formId);
          }
        });
      };

      // To make sure that we still call the function mapped to the 200 response code,
      // we create a parent function that will try to call our updateRequestBodyWithResponse function
      // and will then call the function mapped to the 200 response in the finally block to ensure
      // that it always gets called.
      const functionChain = (response: AxiosResponse) => {
        try {
          updateRequestBodyWithResponse(response);
        } finally {
          if (input.callbacks[200] !== undefined) {
            input.callbacks[200](response);
          }
        }
      };

      const newCallbacks = {
        ...input.callbacks,
        200: functionChain
      };

      await wraxios.post(
        axios,
        input.url as string,
        updateBody,
        newCallbacks
      );
    },

    do_setTranslations(context, payload: SetTranslationsPayload) {
      context.commit("set_translations", payload);
    }
  },

  plugins: [vuexLocal.plugin]  
});